import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import FastlyImage from '@components/common/fastly-image'
import ContentCard from '@components/course-landing-page/content-card'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { HTML } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['wd'].trialCourseQuery}`

export const HTMLSeo = formatSeoConfig({
  title: 'HTML for Kids: Learn Web Development Online',
  description:
    'HTML coding for kids taught by expert instructors. Build and design websites with a structured, K–12 aligned curriculum at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/courses/html-for-kids/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-light-v3.jpg',
})
const Html = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...HTMLSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[HTML].map(({ acceptedAnswerText, questionName }) => ({
          acceptedAnswerText: acceptedAnswerText,
          questionName: questionName,
        }))}
      />
      <CourseJsonLd
        courseName="Learn HTML with Top AI & Coding Course Instructors for Kids and Teens"
        description="Empower your child to move from consuming content to creating it. Codingal’s website development course teaches HTML to kids and teens the building blocks of the web, enabling them to design and structure their own websites. With guidance from expert instructors, students will embark on a journey that combines creativity with foundational coding skills, setting the stage for future exploration in AI & coding."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={HTML}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={HTML} />
          <CourseFeatures
            courseTitle={HTML}
            title={<Trans>Engaging Website Development classes for kids</Trans>}
          />
          <ClassCurriculum
            courseName={HTML}
            title="Our HTML Class Curriculum"
            description={
              <div className="space-y-3">
                <div className="my-4">
                  <FastlyImage
                    src={`/images/courses/html/html-2.jpg`}
                    alt="A student learning HTML concepts, from basic structure to building interactive web pages, like creating a digital LEGO project"
                    width={512}
                    height={341}
                  />
                </div>
                <p>
                  <Trans>
                    The HTML curriculum is designed to take students from
                    foundational concepts to practical web development skills.
                    Beginning with setting up development tools and writing
                    basic HTML, students gradually learn to use headings,
                    paragraphs, lists, links, tables, media, semantic tags, and
                    forms. Each lesson builds on the previous one, enabling
                    students to create structured, interactive web pages and
                    apply their learning in hands-on, real-world projects -
                    it&lsquo;s like building with digital LEGOs!
                  </Trans>
                </p>
              </div>
            }
          />
          <Activities courseName={HTML} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                <p>
                  Our instructors bring deep expertise in Computer Science and
                  complete rigorous training before they teach. They’re truly
                  amazing, and it shows in our exceptional Net Promoter Score
                  (NPS) of 86! Students and parents consistently rate our
                  teachers 4.9 out of 5, and our curriculum earns a fantastic
                  4.8 out of 5. We’re so proud to have a faculty that’s 91%
                  female and 9% male, creating an inclusive, encouraging
                  learning environment where everyone feels welcome. Each tutor
                  is trained to understand individual student needs and deliver
                  personalized support, even in group settings. Plus, students
                  have 24/7 access to expert tutors for instant doubt
                  resolution, anytime they need help. This makes learning HTML a
                  truly supported experience, especially through our virtual
                  HTML lessons that connect kids with top educators worldwide.
                </p>
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals of
                Website Development
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  Codingal offers a nurturing environment where young learners
                  can explore the world of coding without fear. Our structured
                  curriculum, combined with supportive instructors, ensures that
                  students build a strong foundation in HTML, preparing them for
                  exciting future endeavors in AI & coding.
                </span>
              </Trans>
            }
            courseName={HTML}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                <p>
                  At Codingal, your child masters HTML through a STEM.org -
                  accredited program built on proven educational frameworks like
                  Bloom’s Taxonomy, BIDE, and STEAM. Our curriculum is aligned
                  with Common Core, state standards, AP Computer Science goals,
                  and the K - 12 Computer Science Framework - ensuring learning
                  that’s both age-appropriate and college-ready. Through
                  hands-on, project-based learning, students create real-world
                  apps like temperature converters and scientific calculators,
                  applying math, science, and engineering logic to solve
                  everyday problems. Backed by STEM.org’s rigorous audits, every
                  HTML lesson lays a strong, future-proof foundation in STEM.
                </p>
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your HTML certifications"
            description={
              <>
                In the Website Development track, students in grades 6 - 12
                begin their journey with HTML, the foundational language of the
                web. They learn how to structure web pages using elements like
                headings, paragraphs, links, images, lists, tables, and forms.
                As they progress, students apply semantic HTML to improve
                accessibility and build pages that mirror real-world websites.
                Through hands-on lessons and creative projects, they transform
                code into fully functional web pages. By the end of the HTML
                module, students not only understand how websites are built but
                also develop a strong foundation for diving into web design,
                interactivity with JavaScript, and more advanced web
                technologies.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our HTML classes are appropriate for middle school & high school students"
            description={
              <>
                At Codingal, our foundational HTML & CSS curriculum introduces
                middle and high school students to the foundational language of
                the web. Starting with simple elements like headings,
                paragraphs, and links, students engage in beginner-friendly
                activities that teach them how to structure content and create
                web pages. As they progress, they explore more advanced concepts
                such as tables, forms, and semantic elements - ensuring a clear,
                steady learning curve. With each project, students build
                real-world webpages, strengthen their understanding of web
                development, and gain the confidence to advance into styling and
                interactive web designs with HTML & CSS.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['wd', 'cgm']} />
          <ClassCurriculum
            courseName={HTML}
            title="About HTML"
            description={
              <div className="space-y-3">
                <h2 className="font-600 text-lg">
                  Why Should Kids Learn HTML?
                </h2>
                <p>Learning HTML is a great way for kids to:</p>
                <ul>
                  <li>Boost creativity by designing their own websites</li>
                  <li>
                    Learn problem-solving skills by figuring out how to
                    structure and style a page
                  </li>
                  <li>
                    Understand the basics of web development, which is essential
                    for future coding careers
                  </li>
                  <li>Express themselves by building their own web projects</li>
                  <li>
                    HTML gives kids a strong foundation for learning other
                    coding languages like CSS and JavaScript.
                  </li>
                </ul>
                <h2 className="font-600 text-lg">
                  How Do You Explain HTML to a Child?
                </h2>
                <p>
                  Imagine you’re building a house. HTML is like the foundation,
                  walls, and roof; it gives your website structure.CSS is like
                  the paint and furniture that make the house look nice.
                  Explaining it this way makes it easy for kids to grasp the
                  basics of HTML.
                </p>
                <h2 className="font-600 text-lg">What Does HTML Stand For?</h2>
                <div className="my-4">
                  <FastlyImage
                    src={`/images/courses/html/html-2.jpg`}
                    alt="HTML Learning for Kids"
                    width={512}
                    height={341}
                  />
                </div>
                <p>
                  HTML stands for HyperText Markup Language. It&lsquo;s the
                  foundational language for creating web pages. You can think of
                  it as the structure of a house—the walls, rooms, and
                  foundation. This is fundamental for anyone learning HTML for
                  kids.
                </p>
                <h2 className="font-600 text-lg mt-3">
                  An Easy Way to Understand HTML for kids
                </h2>
                <p>
                  Here’s a simple analogy to help you understand HTML. Imagine
                  you are drawing a superhero. The first step is to sketch the
                  basic outline of the character (the head, body, arms, and
                  legs). That simple sketch is your HTML. It provides the
                  essential structure, but it’s plain. It’s what gives the
                  character personality and makes them look unique and powerful.
                  This is exactly how kids learn HTML in our kids HTML classes;
                  making their ideas come to life!
                </p>
                <h2 className="font-600 text-lg mt-3">
                  What Kids Learn with HTML?
                </h2>
                <div className="my-4">
                  <FastlyImage
                    src={`/images/courses/html/html-3.jpg`}
                    alt="HTML Learning for Kids"
                    width={512}
                    height={341}
                  />
                </div>
                <p>
                  When kids learn HTML, they are learning how to be digital
                  architects. It gives them a strong sense of structure and
                  organization. For those learning HTML for kids, understanding
                  these principles is crucial. They discover how to organize
                  content, add images and videos, create links, and even build
                  forms for interactivity. HTML makes visual coding for kids
                  exciting, turning their work into digital blueprints for the
                  web.
                </p>
                <p>Here are a few things they can do with HTML:</p>
                <ol className="space-y-4">
                  <li className="space-y-2">
                    <span>
                      Structure Content: Kids can organize text with headings,
                      paragraphs, and lists - just like organizing their
                      thoughts!
                    </span>
                    <div>
                      <FastlyImage
                        src={`/images/courses/html/structure-content.jpg`}
                        alt="A child organizing text with headings, paragraphs, and lists in HTML"
                        width={512}
                        height={341}
                      />
                    </div>
                  </li>
                  <li className="space-y-2">
                    <span>
                      Add Media: They can embed images, videos, and audio into
                      their web pages - making them super engaging!
                    </span>
                    <div>
                      <FastlyImage
                        src={`/images/courses/html/add-media.jpg`}
                        alt="A child embedding images, videos, and audio files into a web page"
                        width={512}
                        height={341}
                      />
                    </div>
                  </li>
                  <li className="space-y-2">
                    <span>
                      Create Links: They can connect different pages or external
                      websites; building their very own web of information!
                    </span>
                    <div>
                      <FastlyImage
                        src={`/images/courses/html/create-links.jpg`}
                        alt="A child creating clickable links to connect different pages or websites"
                        width={512}
                        height={341}
                      />
                    </div>
                  </li>
                  <li className="space-y-2">
                    <span>
                      Build Forms: They can create interactive elements like
                      contact forms or surveys - making their websites useful
                      and fun!
                    </span>
                    <div>
                      <FastlyImage
                        src={`/images/courses/html/build-forms.jpg`}
                        alt="A child building an interactive contact form or survey for a website"
                        width={512}
                        height={341}
                      />
                    </div>
                  </li>
                </ol>
              </div>
            }
          />
          <Faq courseName={HTML} title="Frequently Asked Questions" />
          <CodingalProductBlog urlQuery={urlQuery} isCoursePage />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={HTML}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="HTML for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Html
